import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Zap, Play, Check, X, Trophy, Clock, Target, Minimize, Settings, Award, Brain, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Time interval options from 5000ms to 500ms
const TIME_INTERVALS = [
  { value: '5', label: '5 seconds (5000ms) - Very Easy', ms: 5000 },
  { value: '4.5', label: '4.5 seconds (4500ms) - Easy', ms: 4500 },
  { value: '4', label: '4 seconds (4000ms)', ms: 4000 },
  { value: '3.5', label: '3.5 seconds (3500ms)', ms: 3500 },
  { value: '3', label: '3 seconds (3000ms) - Medium', ms: 3000 },
  { value: '2.5', label: '2.5 seconds (2500ms)', ms: 2500 },
  { value: '2', label: '2 seconds (2000ms)', ms: 2000 },
  { value: '1.5', label: '1.5 seconds (1500ms) - Hard', ms: 1500 },
  { value: '1', label: '1 second (1000ms) - Very Hard', ms: 1000 },
  { value: '0.75', label: '750ms - Expert', ms: 750 },
  { value: '0.5', label: '500ms - Master', ms: 500 }
];

// Number of digits options
const DIGIT_OPTIONS = [
  { value: '2', label: '2 Digits - Beginner' },
  { value: '3', label: '3 Digits - Easy' },
  { value: '4', label: '4 Digits - Medium' },
  { value: '5', label: '5 Digits - Hard' },
  { value: '6', label: '6 Digits - Very Hard' },
  { value: '7', label: '7 Digits - Expert' },
  { value: '8', label: '8 Digits - Master' },
  { value: '9', label: '9 Digits - Legend' },
  { value: '10', label: '10 Digits - Genius' }
];

// Number of flashes options
const FLASH_COUNT_OPTIONS = [
  { value: '3', label: '3 Numbers' },
  { value: '5', label: '5 Numbers' },
  { value: '10', label: '10 Numbers' },
  { value: '15', label: '15 Numbers' },
  { value: '20', label: '20 Numbers' }
];

const FlashNumberGame = ({ user, examMode = false, examConfig = null, onExamComplete = null, onExit = null }) => {
  const [gameState, setGameState] = useState('config'); // config, ready, playing, input, result
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [config, setConfig] = useState(
    examMode && examConfig ? {
      digitCount: examConfig.numberOfDigits?.toString() || '4',
      flashInterval: (examConfig.flashSpeed / 1000)?.toString() || '1',
      numberOfFlashes: examConfig.numberOfQuestions?.toString() || '5'
    } : {
      digitCount: '4',
      flashInterval: '1',
      numberOfFlashes: '5'
    }
  );
  const [currentFlash, setCurrentFlash] = useState(0);
  const [flashedNumber, setFlashedNumber] = useState('');
  const [allFlashedNumbers, setAllFlashedNumbers] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [showingGap, setShowingGap] = useState(false);
  const autoStarted = useRef(false);
  const gameContainerRef = useRef(null);

  const handleExit = () => {
    if (window.confirm('Are you sure you want to exit the game?')) {
      if (onExit) {
        onExit();
      } else {
        setGameState('config');
        setAllFlashedNumbers([]);
        setCurrentFlash(0);
      }
    }
  };

  // Auto-enter fullscreen when component mounts (not in exam mode)
  useEffect(() => {
    if (!examMode && !autoStarted.current) {
      autoStarted.current = true;
      // Enter fullscreen automatically when game is opened
      setTimeout(() => {
        enterFullScreen();
      }, 300);
    }
  }, []);

  // Auto-start game IMMEDIATELY when in exam mode
  useEffect(() => {
    if (examMode && examConfig?.autoStart && !autoStarted.current) {
      console.log('🎮 EXAM MODE: Starting game immediately!');
      autoStarted.current = true;
      // Start game immediately without any delay
      setGameState('playing');
      setCurrentFlash(0);
      setAllFlashedNumbers([]);
      setStartTime(Date.now());
      // Start flashing after a tiny delay to ensure state is set
      setTimeout(() => {
        flashNextNumber(0, []);
      }, 100);
    }
  }, [examMode, examConfig?.autoStart]);

  const generateRandomNumber = (digits) => {
    const min = Math.pow(10, digits - 1);
    const max = Math.pow(10, digits) - 1;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const enterFullScreen = async () => {
    try {
      if (gameContainerRef.current) {
        if (gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
        } else if (gameContainerRef.current.webkitRequestFullscreen) {
          await gameContainerRef.current.webkitRequestFullscreen();
        } else if (gameContainerRef.current.mozRequestFullScreen) {
          await gameContainerRef.current.mozRequestFullScreen();
        } else if (gameContainerRef.current.msRequestFullscreen) {
          await gameContainerRef.current.msRequestFullscreen();
        }
        setIsFullScreen(true);
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  const exitFullScreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullScreen(false);
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  const proceedToReady = async () => {
    await enterFullScreen();
    setGameState('ready');
  };

  const startGame = () => {
    setGameState('playing');
    setCurrentFlash(0);
    setAllFlashedNumbers([]);
    setStartTime(Date.now());
    flashNextNumber(0, []);
  };

  const flashNextNumber = (flashIndex, numbersArray) => {
    const number = generateRandomNumber(parseInt(config.digitCount));
    const updatedNumbers = [...numbersArray, number];
    setAllFlashedNumbers(updatedNumbers);
    setFlashedNumber(number.toString());
    setIsFlashing(true);
    setShowingGap(false);

    // Flash duration
    setTimeout(() => {
      setIsFlashing(false);
      setFlashedNumber('');
      
      // Check if more numbers to flash
      if (flashIndex + 1 < parseInt(config.numberOfFlashes)) {
        setShowingGap(true);
        setCurrentFlash(flashIndex + 1);
        
        // Gap before next flash (0.5 seconds)
        setTimeout(() => {
          flashNextNumber(flashIndex + 1, updatedNumbers);
        }, 500);
      } else {
        // All numbers flashed, move to input
        setTimeout(() => {
          setGameState('input');
        }, 500);
      }
    }, parseInt(config.flashInterval) * 1000);
  };

  const submitAnswer = () => {
    const correctSum = allFlashedNumbers.reduce((sum, num) => sum + num, 0);
    const isCorrect = parseInt(userAnswer) === correctSum;
    
    showResults(isCorrect, correctSum);
  };

  const showResults = async (isCorrect, correctSum) => {
    setGameState('result');
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    const accuracy = isCorrect ? 100 : 0;
    const score = isCorrect ? 1 : 0;

    const scoreData = {
      game_type: 'flash_number',
      difficulty: `${config.digitCount}_digit`,
      score: score,
      total_questions: 1,
      accuracy: accuracy,
      time_taken: timeTaken,
      game_config: {
        digit_count: config.digitCount,
        flash_interval: config.flashInterval,
        number_of_flashes: config.numberOfFlashes,
        numbers_flashed: allFlashedNumbers,
        correct_sum: correctSum
      }
    };

    // If exam mode, call onExamComplete instead of saving directly
    if (examMode && onExamComplete) {
      onExamComplete(scoreData);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/game-scores`,
        scoreData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Score saved successfully!');
    } catch (error) {
      console.error('Error saving score:', error);
      toast.error('Failed to save score');
    }
  };

  const restartGame = () => {
    setGameState('config');
    setCurrentFlash(0);
    setFlashedNumber('');
    setUserAnswer('');
    setAllFlashedNumbers([]);
  };

  const renderGameContent = () => {
    // Config Screen (skip if in exam mode)
    if (gameState === 'config' && !examMode) {
      return (
        <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900 flex items-center justify-center p-4 overflow-auto">
          <div className="w-full max-w-3xl relative">
            <div className="text-center mb-8 animate-fade-in">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl border-2 border-white/30">
                <Zap className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-5xl font-black text-white mb-2 drop-shadow-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Flash Number Game
              </h2>
              <p className="text-xl text-white/90">Configure your game settings</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20 space-y-6">
              <div>
                <Label className="text-white font-semibold mb-3 block text-lg">Number of Digits</Label>
                <Select value={config.digitCount} onValueChange={(v) => setConfig({...config, digitCount: v})}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white h-14 text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIGIT_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white font-semibold mb-3 block text-lg">Flash Interval</Label>
                <Select value={config.flashInterval} onValueChange={(v) => setConfig({...config, flashInterval: v})}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white h-14 text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_INTERVALS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white font-semibold mb-3 block text-lg">Number of Flashes</Label>
                <Select value={config.numberOfFlashes} onValueChange={(v) => setConfig({...config, numberOfFlashes: v})}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white h-14 text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FLASH_COUNT_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-base text-white/80 mt-3 bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-3">
                  💡 You'll see {config.numberOfFlashes} numbers flash, then calculate their sum!
                </p>
              </div>

              <Button 
                onClick={proceedToReady}
                className="w-full py-7 text-xl font-bold bg-white text-purple-600 hover:bg-white/90 shadow-xl hover:scale-105 transition-transform"
              >
                Continue →
              </Button>
            </div>
          </div>
        </div>
      );
    }

  // Ready Screen (Start Button) - skip if in exam mode
  if (gameState === 'ready' && !examMode) {
    return (
      <Card className="p-8 max-w-2xl mx-auto bg-white/10 backdrop-blur-lg border-2 border-white/20 shadow-2xl">
        <div className="text-center space-y-8">
          <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full w-24 h-24 mx-auto flex items-center justify-center shadow-2xl">
            <Zap className="w-12 h-12 text-white" />
          </div>

          <div>
            <h2 className="text-3xl font-bold mb-3 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Ready to Start?
            </h2>
            <p className="text-white/90 text-lg">Prepare yourself for the challenge!</p>
          </div>

          <div className="bg-white/10 border-2 border-white/20 backdrop-blur-lg rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-lg text-white">Game Settings:</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg border border-white/30">
                <div className="text-3xl font-bold text-white">{config.digitCount}</div>
                <div className="text-xs text-white/80 mt-1">Digit{config.digitCount > 1 ? 's' : ''}</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg border border-white/30">
                <div className="text-3xl font-bold text-white">{config.flashInterval}s</div>
                <div className="text-xs text-white/80 mt-1">Flash Time</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg border border-white/30">
                <div className="text-3xl font-bold text-white">{config.numberOfFlashes}</div>
                <div className="text-xs text-white/80 mt-1">Numbers</div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-500/20 border border-yellow-400/30 backdrop-blur-lg rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">💡</div>
              <div className="text-left">
                <h4 className="font-semibold text-white mb-1">How to Play:</h4>
                <ul className="text-sm text-white/90 space-y-1">
                  <li>• {config.numberOfFlashes} numbers will flash one by one</li>
                  <li>• Each number appears for {config.flashInterval} second{config.flashInterval > 1 ? 's' : ''}</li>
                  <li>• Remember all the numbers</li>
                  <li>• Calculate and enter their sum at the end</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={restartGame} 
              variant="outline" 
              className="flex-1 py-6 text-lg"
            >
              ← Back to Settings
            </Button>
            <Button 
              onClick={startGame}
              className="flex-1 py-6 text-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Game!
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Playing Screen (Flashing Number)
  if (gameState === 'playing') {
    return (
      <div className="w-full max-w-5xl px-4">
        <div className="text-center space-y-8">
          <div className="space-y-3">
            <div className="text-xl text-white/90 font-semibold">
              Flash {currentFlash + 1} of {config.numberOfFlashes}
            </div>
            <div className="w-full bg-white/20 rounded-full h-3 backdrop-blur-lg">
              <div 
                className="bg-gradient-to-r from-green-400 to-emerald-400 h-3 rounded-full transition-all duration-300 shadow-lg"
                style={{ width: `${((currentFlash + 1) / parseInt(config.numberOfFlashes)) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="min-h-[400px] bg-white/10 backdrop-blur-xl rounded-3xl border-2 border-white/20 shadow-2xl flex items-center justify-center">
            {isFlashing ? (
              <div className="text-[15rem] font-black text-white drop-shadow-2xl animate-pulse">
                {flashedNumber}
              </div>
            ) : showingGap ? (
              <div className="text-4xl text-white/60 animate-pulse font-semibold">
                Next number coming...
              </div>
            ) : (
              <div className="text-4xl text-white/60 font-semibold">
                Get Ready...
              </div>
            )}
          </div>

          <div className="space-y-2 bg-white/10 backdrop-blur-lg p-6 rounded-2xl border border-white/20">
            <div className="text-2xl font-bold text-white">
              Remember all the numbers!
            </div>
            <div className="text-lg text-white/80">
              You'll need to calculate their sum
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input Screen
  if (gameState === 'input') {
    return (
      <div className="w-full max-w-4xl px-4">
        <div className="text-center space-y-8">
          <div className="p-6 bg-white/10 backdrop-blur-lg rounded-2xl border-2 border-white/20 shadow-2xl">
            <div className="text-lg text-white/90 mb-2 font-semibold">Numbers Flashed:</div>
            <div className="text-4xl font-black text-white">{config.numberOfFlashes} numbers</div>
          </div>
          
          <div className="space-y-3">
            <h3 className="text-5xl font-black text-white drop-shadow-2xl">Calculate the Sum</h3>
            <p className="text-xl text-white/90">What is the total of all the numbers you saw?</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border-2 border-white/20 p-8 shadow-2xl">
            <Input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Enter the sum"
              className="text-center text-6xl py-10 font-black bg-white/20 border-2 border-white/40 text-white placeholder:text-white/30"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter' && userAnswer) {
                  submitAnswer();
                }
              }}
            />
          </div>

          <Button 
            onClick={submitAnswer}
            disabled={!userAnswer}
            className="w-full py-8 text-2xl font-bold bg-white text-purple-600 hover:bg-white/90 disabled:bg-white/20 disabled:text-white/40 rounded-2xl shadow-2xl hover:scale-105 transition-all"
          >
            Submit Answer →
          </Button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === 'result') {
    const correctSum = allFlashedNumbers.reduce((sum, num) => sum + num, 0);
    const isCorrect = parseInt(userAnswer) === correctSum;
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    return (
      <div className="w-full max-w-5xl px-4 py-6">
        <div className="text-center space-y-6 animate-fade-in">
          <div className={`p-6 rounded-full w-28 h-28 mx-auto flex items-center justify-center shadow-2xl ${
            isCorrect 
              ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
              : 'bg-gradient-to-br from-red-500 to-rose-500'
          }`}>
            {isCorrect ? (
              <Check className="w-14 h-14 text-white" />
            ) : (
              <X className="w-14 h-14 text-white" />
            )}
          </div>
          
          <h2 className="text-6xl font-black text-white drop-shadow-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {isCorrect ? 'Excellent! 🎉' : 'Good Try!'}
          </h2>

          <div className="grid grid-cols-3 gap-4 my-8">
            <div className={`p-6 rounded-2xl backdrop-blur-lg border-2 shadow-2xl ${isCorrect ? 'bg-green-500/20 border-green-400/30' : 'bg-red-500/20 border-red-400/30'}`}>
              <Target className={`w-10 h-10 mx-auto mb-3 ${isCorrect ? 'text-green-300' : 'text-red-300'}`} />
              <div className={`text-4xl font-black mb-2 ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </div>
              <div className="text-base text-white/80 font-semibold">Result</div>
            </div>
            <div className="bg-blue-500/20 border-2 border-blue-400/30 backdrop-blur-lg p-6 rounded-2xl shadow-2xl">
              <Check className="w-10 h-10 text-blue-300 mx-auto mb-3" />
              <div className="text-5xl font-black text-blue-300 mb-2">{isCorrect ? 100 : 0}%</div>
              <div className="text-base text-white/80 font-semibold">Accuracy</div>
            </div>
            <div className="bg-purple-500/20 border-2 border-purple-400/30 backdrop-blur-lg p-6 rounded-2xl shadow-2xl">
              <Clock className="w-10 h-10 text-purple-300 mx-auto mb-3" />
              <div className="text-5xl font-black text-purple-300 mb-2">{timeTaken}s</div>
              <div className="text-base text-white/80 font-semibold">Time</div>
            </div>
          </div>

          {/* Show all flashed numbers */}
          <div className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl border-2 border-white/20 shadow-2xl max-h-96 overflow-y-auto">
            <h3 className="font-bold mb-6 text-2xl text-white">Numbers Flashed:</h3>
            <div className="flex flex-wrap gap-4 justify-center mb-6">
              {allFlashedNumbers.map((num, idx) => (
                <div key={idx} className="bg-white/20 backdrop-blur-sm px-6 py-4 rounded-xl border-2 border-white/30 shadow-lg">
                  <div className="text-sm text-white/70 mb-1 font-semibold">#{idx + 1}</div>
                  <div className="text-3xl font-black text-white">{num}</div>
                </div>
              ))}
            </div>
            
            <div className="border-t-2 border-dashed border-white/30 pt-6 mt-6">
              <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                <div className="bg-green-500/20 p-6 rounded-xl border-2 border-green-400/30 backdrop-blur-sm shadow-xl">
                  <div className="text-base text-white/80 mb-2 font-semibold">Correct Sum</div>
                  <div className="text-5xl font-black text-green-300">{correctSum}</div>
                </div>
                <div className={`p-6 rounded-xl border-2 backdrop-blur-sm shadow-xl ${
                  isCorrect 
                    ? 'bg-green-500/20 border-green-400/30' 
                    : 'bg-red-500/20 border-red-400/30'
                }`}>
                  <div className="text-base text-white/80 mb-2 font-semibold">Your Answer</div>
                  <div className={`text-5xl font-black ${
                    isCorrect ? 'text-green-300' : 'text-red-300'
                  }`}>
                    {userAnswer || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            {!examMode && (
              <Button 
                onClick={restartGame} 
                size="lg"
                className="flex-1 py-8 text-xl font-bold bg-white/20 text-white border-2 border-white/30 hover:bg-white/30 rounded-2xl shadow-2xl hover:scale-105 transition-all"
              >
                Change Settings
              </Button>
            )}
            <Button 
              onClick={startGame} 
              size="lg"
              className="flex-1 py-8 text-xl font-bold bg-white text-purple-600 hover:bg-white/90 rounded-2xl shadow-2xl hover:scale-105 transition-all"
            >
              <Play className="w-6 h-6 mr-2" />
              Play Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

    return null;
  };

  // Main render: Wrap all game content in fullscreen container
  return (
    <div ref={gameContainerRef} className={`${isFullScreen ? 'fixed inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900 z-50' : ''} flex items-center justify-center p-4`}>
      {/* Exit fullscreen button */}
      {isFullScreen && !examMode && (
        <button
          onClick={async () => {
            await exitFullScreen();
            if (onExit) {
              onExit();
            } else {
              restartGame();
            }
          }}
          className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50 flex items-center gap-2"
        >
          <Minimize className="w-4 h-4" />
          Exit Game
        </button>
      )}
      
      {renderGameContent()}
    </div>
  );
};

export default FlashNumberGame;
